import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  Box,
  Text,
  Select,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { useState, useEffect } from "react";
import { ChecklistItemViewModel } from "./types";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { useRoles } from "hooks/useRoles";
import { apiRequest } from "utils/api";

interface AssignmentsModalProps {
  checklistItem: ChecklistItemViewModel;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (checklistItem: ChecklistItemViewModel) => void;
}

interface Role {
  id: string;
  name: string;
  description?: string;
  company_type: string;
  displayName?: string;
}

export function AssignmentsModal({
  checklistItem,
  isOpen,
  onClose,
  onUpdate,
}: AssignmentsModalProps) {
  const { user } = useTenant();
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const { roles: lenderRoles, isLoading: isLenderRolesLoading } = useRoles({
    userType: "lender",
    company_id: user.company.id,
  });
  const { roles: borrowerRoles, isLoading: isBorrowerRolesLoading } = useRoles({
    userType: "borrower",
    company_id: user.company.id,
  });

  const allRoles = [...(lenderRoles || []), ...(borrowerRoles || [])].map(role => ({
    ...role,
    displayName: `${role.company_type} - ${role.name}`,
  }));
  const isRolesLoading = isLenderRolesLoading || isBorrowerRolesLoading;

  useEffect(() => {
    if (isOpen && checklistItem.assignments) {
      setSelectedRoles(checklistItem.assignments);
    }
  }, [isOpen, checklistItem]);

  const handleRoleSelect = (roleId: string) => {
    if (!selectedRoles.includes(roleId)) {
      setSelectedRoles([...selectedRoles, roleId]);
    }
  };

  const handleRoleRemove = (roleId: string) => {
    setSelectedRoles(selectedRoles.filter(id => id !== roleId));
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      await apiRequest("/supabase/checklist_items/update", {
        id: checklistItem.id,
        assignments: selectedRoles,
      });

      const updatedItem = {
        ...checklistItem,
        assignments: selectedRoles,
      };
      onUpdate(updatedItem);
      onClose();
    } catch (error) {
      console.error("Failed to update role assignments:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Manage Role Assignments</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text>Select roles to assign to this checklist item:</Text>
            <Select
              placeholder="Select roles"
              onChange={e => handleRoleSelect(e.target.value)}
              isDisabled={isRolesLoading}
            >
              {allRoles.map((role: Role) => (
                <option key={role.id} value={role.id} disabled={selectedRoles.includes(role.id)}>
                  {role.displayName}
                </option>
              ))}
            </Select>

            <Wrap spacing={2}>
              {selectedRoles.map(roleId => {
                const role = allRoles.find((r: Role) => r.id === roleId);
                if (!role) return null;
                return (
                  <WrapItem key={roleId}>
                    <Tag size="md" borderRadius="full" variant="solid" colorScheme="blue">
                      <TagLabel>{role.displayName}</TagLabel>
                      <TagCloseButton onClick={() => handleRoleRemove(roleId)} />
                    </Tag>
                  </WrapItem>
                );
              })}
            </Wrap>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme="blue" onClick={handleSubmit} isLoading={isLoading}>
            Save Role Assignments
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
